import { useState, useMemo } from "react";
import { PDFViewer, pdf } from "@react-pdf/renderer";
import { AcademicCV } from "../../components/cv/AcademicCV";
import {
  type CVSettings,
  type CVTemplateId,
  type CVPageSize,
  CV_COLOR_PRESETS,
  DEFAULT_CV_SETTINGS,
} from "../../components/cv/cv.types";
import { trpc } from "../../lib/trpc";
import { Button } from "@/components/ui/button";
import { getPublicCVUrl } from "@/lib/utils/url";
import {
  Download,
  UploadCloud,
  FileText,
  CheckCircle2,
  ExternalLink,
  Copy,
  Loader2,
  AlertCircle,
  Eye,
  EyeOff,
  ArrowUp,
  ArrowDown,
  Sparkles,
  Palette,
  RotateCcw,
  Sliders,
  Check,
  GraduationCap,
  Briefcase,
  BookOpen,
} from "lucide-react";
import { toast } from "sonner";

export const CVBuilderPage = () => {
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasPdfError, setHasPdfError] = useState(false);

  // Fetch user profile, sections, and existing published CV
  const { data: profile, isLoading: isProfileLoading } = trpc.profile.getMyProfile.useQuery();
  const { data: sections, isLoading: isSectionsLoading } = trpc.profile.getSections.useQuery();
  const { data: existingCV } = trpc.cv.getMyCV.useQuery();
  const utils = trpc.useUtils();

  const getUploadUrl = trpc.cv.getUploadUrl.useMutation();
  const confirmUpload = trpc.cv.confirmUpload.useMutation();

  // Active CV Settings state
  const [settings, setSettings] = useState<CVSettings>(DEFAULT_CV_SETTINGS);
  const [activeTab, setActiveTab] = useState<"template" | "sections" | "details">("template");
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);

  // Initialize or align includedSectionIds with the user's available sections
  const effectiveIncludedIds = useMemo(() => {
    if (!sections || sections.length === 0) return [];
    if (settings.includedSectionIds.length > 0) {
      return settings.includedSectionIds;
    }
    return sections.filter((s) => s.visible).map((s) => s.id);
  }, [sections, settings.includedSectionIds]);

  // Unique preview key forces PDFViewer to re-render in real-time on any setting or order change
  const previewKey = useMemo(() => {
    return [
      settings.template,
      settings.fontFamily,
      settings.accentColor,
      settings.pageSize,
      settings.density,
      settings.citationStyle,
      settings.showCvHeaderBanner,
      settings.showEmail,
      settings.showPhone,
      settings.showLocation,
      settings.showWebsite,
      settings.showScholar,
      settings.showOrcid,
      settings.showLinkedin,
      settings.showGithub,
      settings.showAbstracts,
      settings.showGrantsAmount,
      settings.showCourseCodes,
      settings.showPublicationBadges,
      effectiveIncludedIds.join("-"),
      JSON.stringify(settings.customSectionTitles),
    ].join("_");
  }, [settings, effectiveIncludedIds]);

  const updateSetting = <K extends keyof CVSettings>(key: K, value: CVSettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetSettings = () => {
    if (!sections) return;
    setSettings({
      ...DEFAULT_CV_SETTINGS,
      includedSectionIds: sections.filter((s) => s.visible).map((s) => s.id),
    });
    toast.success("Settings reset to standard academic defaults");
  };

  // Smart Academic Career Presets
  const applyAcademicPreset = (presetType: "faculty" | "research" | "industry") => {
    if (!sections) return;

    let targetOrder: string[] = [];
    if (presetType === "faculty") {
      targetOrder = ["about", "experience", "publications", "projects", "teaching", "research", "skills", "contact"];
    } else if (presetType === "research") {
      targetOrder = ["research", "publications", "projects", "experience", "teaching", "skills", "about", "contact"];
    } else {
      targetOrder = ["about", "experience", "projects", "skills", "publications", "teaching", "research", "contact"];
    }

    const sortedSectionIds: string[] = [];
    for (const type of targetOrder) {
      const match = sections.find((s) => s.type === type && s.visible);
      if (match && !sortedSectionIds.includes(match.id)) {
        sortedSectionIds.push(match.id);
      }
    }
    // Include remaining visible sections
    for (const sec of sections) {
      if (sec.visible && !sortedSectionIds.includes(sec.id)) {
        sortedSectionIds.push(sec.id);
      }
    }

    setSettings((prev) => ({
      ...prev,
      includedSectionIds: sortedSectionIds,
      citationStyle: presetType === "industry" ? "standard" : "numbered",
    }));

    toast.success(
      presetType === "faculty"
        ? "Applied 'Faculty & Tenure' academic hierarchy"
        : presetType === "research"
        ? "Applied 'Research & Grants' academic hierarchy"
        : "Applied 'Industry & Consulting' professional hierarchy"
    );
  };

  // Section Ordering and Visibility Helpers
  const toggleSection = (sectionId: string) => {
    const isCurrentlyIncluded = effectiveIncludedIds.includes(sectionId);
    let next: string[];
    if (isCurrentlyIncluded) {
      next = effectiveIncludedIds.filter((id) => id !== sectionId);
    } else {
      next = [...effectiveIncludedIds, sectionId];
    }
    updateSetting("includedSectionIds", next);
  };

  const moveSection = (index: number, direction: "up" | "down") => {
    const list = [...effectiveIncludedIds];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= list.length) return;
    const temp = list[index];
    list[index] = list[targetIndex];
    list[targetIndex] = temp;
    updateSetting("includedSectionIds", list);
  };

  const handleTitleChange = (sectionId: string, newTitle: string) => {
    setSettings((prev) => ({
      ...prev,
      customSectionTitles: {
        ...prev.customSectionTitles,
        [sectionId]: newTitle,
      },
    }));
  };

  // Download PDF Handler
  const handleDownload = async () => {
    if (!profile || !sections) return;
    setIsDownloading(true);
    const toastId = toast.loading("Compiling publication-grade PDF document...");

    try {
      const blob = await pdf(
        <AcademicCV
          profile={profile}
          sections={sections}
          settings={{ ...settings, includedSectionIds: effectiveIncludedIds }}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeName = (profile.fullName || "Academic").replace(/[^a-zA-Z0-9_-]/g, "_");
      link.href = url;
      link.download = `${safeName}_CV.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded successfully!", { id: toastId });
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Failed to download PDF", { id: toastId });
    } finally {
      setIsDownloading(false);
    }
  };

  // Publish to Cloud Storage (R2) Handler
  const handlePublish = async () => {
    if (!profile || !sections) return;
    setIsPublishing(true);
    const loadingToast = toast.loading("Generating PDF bundle...");

    try {
      // 1. Generate Blob in-browser
      const blob = await pdf(
        <AcademicCV
          profile={profile}
          sections={sections}
          settings={{ ...settings, includedSectionIds: effectiveIncludedIds }}
        />
      ).toBlob();

      toast.loading("Requesting secure upload authorization...", { id: loadingToast });

      // 2. Call trpc.cv.getUploadUrl to get presigned R2 URL and shareToken
      const activeTypes = sections
        .filter((s) => effectiveIncludedIds.includes(s.id))
        .map((s) => s.type);

      const { url, shareToken } = await getUploadUrl.mutateAsync({
        includedSections: activeTypes.length > 0 ? activeTypes : ["about"],
        template: settings.template,
      });

      toast.loading("Uploading to cloud CDN...", { id: loadingToast });

      // 3. Upload Blob via HTTP PUT
      const uploadRes = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/pdf",
        },
        body: blob,
      });

      if (!uploadRes.ok) {
        throw new Error("Failed to upload PDF");
      }

      toast.loading("Publishing document metadata...", { id: loadingToast });

      // 4. Confirm upload to save metadata
      await confirmUpload.mutateAsync({ shareToken });

      toast.success("CV published online successfully!", { id: loadingToast });
      utils.cv.getMyCV.invalidate();
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || "Failed to publish CV", { id: loadingToast });
    } finally {
      setIsPublishing(false);
    }
  };

  const copyShareLink = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard!");
  };

  if (isProfileLoading || isSectionsLoading) {
    return (
      <div className="max-w-7xl mx-auto py-12 px-4 flex flex-col items-center justify-center min-h-[60vh] gap-3">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
        <p className="text-xs text-muted-foreground font-medium">Loading Academic CV Studio...</p>
      </div>
    );
  }

  if (!profile || !sections) {
    return (
      <div className="max-w-7xl mx-auto py-8 px-4">
        <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-6 text-center space-y-2">
          <AlertCircle className="h-8 w-8 text-destructive mx-auto" />
          <h2 className="text-sm font-semibold text-foreground">Failed to load profile data</h2>
          <p className="text-xs text-muted-foreground">
            Please make sure your profile exists and try refreshing the page.
          </p>
        </div>
      </div>
    );
  }

  const templates: Array<{
    id: CVTemplateId;
    title: string;
    description: string;
    tag: string;
  }> = [
    {
      id: "academic-classic",
      title: "Academic Classic",
      description: "Ivy League & Harvard standard serif formatting, centered header, traditional citation layout.",
      tag: "Standard",
    },
    {
      id: "modern-executive",
      title: "Modern Executive",
      description: "Contemporary Swiss typography with accent-colored left rules and structured metadata chips.",
      tag: "Popular",
    },
    {
      id: "minimal-clean",
      title: "Minimal Clean",
      description: "Architectural whitespace, delicate hair lines, and understated typography.",
      tag: "Refined",
    },
    {
      id: "compact-two-column",
      title: "Compact Two-Column",
      description: "Sidebar summary for contact, research interests & skills; wide column for appointments & publications.",
      tag: "Modern",
    },
  ];

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 space-y-6">
      {/* ── Studio Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/60 pb-5">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">Academic CV Studio</h1>
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
              Interactive Builder
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Build, format, and download your publication-ready Curriculum Vitae with instant live preview.
          </p>
        </div>

        <div className="flex items-center gap-2.5 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            disabled={isDownloading || isPublishing}
            className="text-xs h-9 gap-1.5 shadow-2xs font-medium"
          >
            {isDownloading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
            <span>Download PDF</span>
          </Button>

          <Button
            size="sm"
            onClick={handlePublish}
            disabled={isPublishing || isDownloading}
            className="text-xs h-9 gap-1.5 font-medium shadow-2xs"
          >
            {isPublishing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <UploadCloud className="h-3.5 w-3.5" />
            )}
            <span>{isPublishing ? "Publishing..." : "Generate & Publish Online"}</span>
          </Button>
        </div>
      </div>

      {/* ── Published Status Banner ── */}
      {existingCV && (() => {
        const permanentCVUrl = getPublicCVUrl(profile?.subdomain, (profile as any)?.customDomain);
        return (
          <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 shadow-2xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-600 shrink-0">
                <CheckCircle2 className="h-4 w-4" />
              </div>
              <div>
                <div className="font-semibold text-foreground flex items-center gap-1.5">
                  <span>CV Published & Live</span>
                  <span className="text-[10px] bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 font-mono px-1.5 py-0.2 rounded font-semibold">
                    Permanent Link
                  </span>
                </div>
                <div className="text-muted-foreground text-[11px] flex flex-wrap items-center gap-1.5 mt-0.5">
                  <span className="font-mono text-foreground font-medium select-all">
                    {permanentCVUrl}
                  </span>
                  <span>•</span>
                  <span>Last synchronized: {new Date(existingCV.generatedAt).toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyShareLink(permanentCVUrl)}
                className="text-xs h-8 gap-1.5 shadow-2xs"
              >
                <Copy className="h-3 w-3" />
                <span>Copy Permanent Link</span>
              </Button>
              <a
                href={permanentCVUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-foreground text-background text-xs font-medium hover:opacity-90 transition-opacity shadow-2xs"
              >
                <ExternalLink className="h-3 w-3" />
                <span>Open Live Document</span>
              </a>
            </div>
          </div>
        );
      })()}

      {/* ── Main Studio Grid: Left Control Panel + Right Live Preview ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* ── Left Controls Panel (5 Columns) ── */}
        <div className="lg:col-span-5 space-y-4">
          <div className="rounded-xl border border-border/80 bg-card shadow-xs overflow-hidden">
            {/* Studio Navigation Tabs */}
            <div className="flex border-b border-border/60 bg-muted/30 p-1 gap-1 text-xs font-medium">
              <button
                type="button"
                onClick={() => setActiveTab("template")}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg transition-colors ${
                  activeTab === "template"
                    ? "bg-card text-foreground font-semibold shadow-2xs border border-border/50"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Palette className="h-3.5 w-3.5" />
                <span>Theme & Style</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab("sections")}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg transition-colors ${
                  activeTab === "sections"
                    ? "bg-card text-foreground font-semibold shadow-2xs border border-border/50"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Sliders className="h-3.5 w-3.5" />
                <span>Sections ({effectiveIncludedIds.length})</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab("details")}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg transition-colors ${
                  activeTab === "details"
                    ? "bg-card text-foreground font-semibold shadow-2xs border border-border/50"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Eye className="h-3.5 w-3.5" />
                <span>Academic Layout</span>
              </button>
            </div>

            <div className="p-4 space-y-5">
              {/* Tab 1: Template & Typography */}
              {activeTab === "template" && (
                <div className="space-y-5">
                  {/* Template Cards */}
                  <div>
                    <label className="text-xs font-semibold text-foreground flex items-center gap-1.5 mb-2.5">
                      <Sparkles className="h-3.5 w-3.5 text-primary" />
                      Academic Layout Template
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      {templates.map((tpl) => {
                        const isSelected = settings.template === tpl.id;
                        return (
                          <button
                            key={tpl.id}
                            type="button"
                            onClick={() => updateSetting("template", tpl.id)}
                            className={`text-left p-3 rounded-xl border text-xs transition-all relative flex flex-col justify-between ${
                              isSelected
                                ? "border-primary bg-primary/5 ring-1 ring-primary/40 shadow-xs"
                                : "border-border/70 hover:border-border hover:bg-muted/40"
                            }`}
                          >
                            <div>
                              <div className="flex items-center justify-between gap-1 mb-1">
                                <span className="font-semibold text-foreground">{tpl.title}</span>
                                <span className="text-[10px] font-medium px-1.5 py-0.2 rounded-full bg-muted text-muted-foreground">
                                  {tpl.tag}
                                </span>
                              </div>
                              <p className="text-[11px] text-muted-foreground line-clamp-2 leading-relaxed">
                                {tpl.description}
                              </p>
                            </div>
                            {isSelected && (
                              <div className="mt-2 flex items-center gap-1 text-[11px] font-semibold text-primary">
                                <Check className="h-3 w-3" />
                                <span>Selected</span>
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Typography Font Choice */}
                  <div>
                    <label className="text-xs font-semibold text-foreground flex items-center justify-between mb-2">
                      <span>Typography Font Family</span>
                      <span className="text-[10px] text-muted-foreground font-normal">
                        Pre-rendered standard PostScript
                      </span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => updateSetting("fontFamily", "times")}
                        className={`p-2.5 rounded-lg border text-left text-xs transition-all ${
                          settings.fontFamily === "times"
                            ? "border-primary bg-primary/5 font-semibold text-primary"
                            : "border-border/70 text-muted-foreground hover:bg-muted/40"
                        }`}
                      >
                        <div className="font-serif text-sm text-foreground">Times Roman</div>
                        <div className="text-[10px] text-muted-foreground">Traditional Academic Serif</div>
                      </button>

                      <button
                        type="button"
                        onClick={() => updateSetting("fontFamily", "helvetica")}
                        className={`p-2.5 rounded-lg border text-left text-xs transition-all ${
                          settings.fontFamily === "helvetica"
                            ? "border-primary bg-primary/5 font-semibold text-primary"
                            : "border-border/70 text-muted-foreground hover:bg-muted/40"
                        }`}
                      >
                        <div className="font-sans text-sm text-foreground">Helvetica</div>
                        <div className="text-[10px] text-muted-foreground">Contemporary Swiss Sans</div>
                      </button>
                    </div>
                  </div>

                  {/* Color Accent Presets */}
                  <div>
                    <label className="text-xs font-semibold text-foreground mb-2 block">
                      Accent Color Palette
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {CV_COLOR_PRESETS.map((preset) => {
                        const isSelected = settings.accentColor.toLowerCase() === preset.primary.toLowerCase();
                        return (
                          <button
                            key={preset.id}
                            type="button"
                            onClick={() => updateSetting("accentColor", preset.primary)}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs transition-all ${
                              isSelected
                                ? "border-primary bg-primary/10 font-semibold shadow-2xs"
                                : "border-border/70 hover:bg-muted/40 text-muted-foreground"
                            }`}
                          >
                            <span
                              className="h-3.5 w-3.5 rounded-full border border-black/10 shrink-0"
                              style={{ backgroundColor: preset.primary }}
                            />
                            <span>{preset.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Paper Format & Spacing Density */}
                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <div>
                      <label className="text-xs font-semibold text-foreground mb-1.5 block">
                        Page Dimensions
                      </label>
                      <div className="flex gap-1.5">
                        {(["A4", "LETTER"] as CVPageSize[]).map((size) => (
                          <button
                            key={size}
                            type="button"
                            onClick={() => updateSetting("pageSize", size)}
                            className={`flex-1 py-1.5 px-2 rounded-lg border text-center text-xs transition-all ${
                              settings.pageSize === size
                                ? "border-primary bg-primary/10 font-semibold text-foreground"
                                : "border-border/70 text-muted-foreground hover:bg-muted/40"
                            }`}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-foreground mb-1.5 block">
                        Document Density
                      </label>
                      <div className="flex gap-1.5">
                        {(["standard", "compact"] as const).map((d) => (
                          <button
                            key={d}
                            type="button"
                            onClick={() => updateSetting("density", d)}
                            className={`flex-1 py-1.5 px-2 rounded-lg border text-center text-xs capitalize transition-all ${
                              settings.density === d
                                ? "border-primary bg-primary/10 font-semibold text-foreground"
                                : "border-border/70 text-muted-foreground hover:bg-muted/40"
                            }`}
                          >
                            {d}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab 2: Section Ordering & Visibility */}
              {activeTab === "sections" && (
                <div className="space-y-4">
                  {/* Smart Academic Ordering Presets */}
                  <div>
                    <span className="text-xs font-semibold text-foreground block mb-1.5">
                      Academic Career Presets
                    </span>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        onClick={() => applyAcademicPreset("faculty")}
                        className="p-2 rounded-lg border border-border/70 bg-card hover:border-primary/50 text-left text-[11px] transition-all"
                      >
                        <div className="flex items-center gap-1 font-semibold text-foreground mb-0.5">
                          <GraduationCap className="h-3 w-3 text-primary" />
                          <span>Tenure Track</span>
                        </div>
                        <span className="text-[10px] text-muted-foreground line-clamp-1">Appointments first</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => applyAcademicPreset("research")}
                        className="p-2 rounded-lg border border-border/70 bg-card hover:border-primary/50 text-left text-[11px] transition-all"
                      >
                        <div className="flex items-center gap-1 font-semibold text-foreground mb-0.5">
                          <BookOpen className="h-3 w-3 text-primary" />
                          <span>Grant / Lab</span>
                        </div>
                        <span className="text-[10px] text-muted-foreground line-clamp-1">Research & Pubs first</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => applyAcademicPreset("industry")}
                        className="p-2 rounded-lg border border-border/70 bg-card hover:border-primary/50 text-left text-[11px] transition-all"
                      >
                        <div className="flex items-center gap-1 font-semibold text-foreground mb-0.5">
                          <Briefcase className="h-3 w-3 text-primary" />
                          <span>Advisory</span>
                        </div>
                        <span className="text-[10px] text-muted-foreground line-clamp-1">Executive Summary</span>
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs pt-2 border-t border-border/50">
                    <span className="font-semibold text-foreground">Section Hierarchy</span>
                    <span className="text-[11px] text-muted-foreground">Click title to rename</span>
                  </div>

                  <div className="space-y-1.5">
                    {effectiveIncludedIds.map((id, index) => {
                      const sec = sections.find((s) => s.id === id);
                      if (!sec) return null;
                      const title = settings.customSectionTitles[id] || (sec.content as any)?.heading || sec.type;
                      const isEditing = editingTitleId === id;

                      return (
                        <div
                          key={id}
                          className="flex items-center justify-between p-2.5 rounded-lg border border-border/70 bg-card hover:bg-muted/30 transition-colors text-xs"
                        >
                          <div className="flex items-center gap-2.5 min-w-0 flex-1 mr-2">
                            <span className="text-[10px] font-mono text-muted-foreground w-4 text-center">
                              {index + 1}
                            </span>
                            <div className="truncate flex-1">
                              {isEditing ? (
                                <input
                                  type="text"
                                  defaultValue={title}
                                  autoFocus
                                  onBlur={(e) => {
                                    handleTitleChange(id, e.target.value.trim() || title);
                                    setEditingTitleId(null);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      handleTitleChange(id, (e.target as HTMLInputElement).value.trim() || title);
                                      setEditingTitleId(null);
                                    }
                                  }}
                                  className="w-full text-xs font-semibold bg-background px-1.5 py-0.5 rounded border border-primary focus:outline-hidden"
                                />
                              ) : (
                                <span
                                  onClick={() => setEditingTitleId(id)}
                                  className="font-semibold text-foreground capitalize block truncate cursor-pointer hover:underline"
                                  title="Click to rename"
                                >
                                  {title}
                                </span>
                              )}
                              <span className="text-[10px] text-muted-foreground capitalize">
                                Type: {sec.type}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => moveSection(index, "up")}
                              disabled={index === 0}
                              className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30"
                              title="Move up"
                            >
                              <ArrowUp className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveSection(index, "down")}
                              disabled={index === effectiveIncludedIds.length - 1}
                              className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30"
                              title="Move down"
                            >
                              <ArrowDown className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleSection(id)}
                              className="p-1 rounded text-destructive hover:bg-destructive/10 ml-1"
                              title="Exclude from CV"
                            >
                              <EyeOff className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {/* Excluded sections */}
                    {sections
                      .filter((s) => !effectiveIncludedIds.includes(s.id))
                      .map((sec) => (
                        <div
                          key={sec.id}
                          className="flex items-center justify-between p-2 rounded-lg border border-dashed border-border/60 bg-muted/20 text-xs opacity-60 hover:opacity-100 transition-opacity"
                        >
                          <div className="flex items-center gap-2 truncate">
                            <span className="text-[10px] text-muted-foreground line-through">
                              {(sec.content as any)?.heading || sec.type}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleSection(sec.id)}
                            className="text-[11px] font-medium text-primary hover:underline flex items-center gap-1"
                          >
                            <Eye className="h-3 w-3" />
                            <span>Include</span>
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Tab 3: Detailed Formatting & Academic Controls */}
              {activeTab === "details" && (
                <div className="space-y-4">
                  {/* Academic Bibliographic Citation Style */}
                  <div>
                    <span className="text-xs font-semibold text-foreground block mb-2">
                      Publications Citation Style
                    </span>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <button
                        type="button"
                        onClick={() => updateSetting("citationStyle", "numbered")}
                        className={`p-2.5 rounded-lg border text-left transition-all ${
                          settings.citationStyle === "numbered"
                            ? "border-primary bg-primary/5 font-semibold text-primary"
                            : "border-border/70 text-muted-foreground hover:bg-muted/40"
                        }`}
                      >
                        <div className="text-foreground font-medium">Numbered [1], [2]</div>
                        <div className="text-[10px] text-muted-foreground">Standard Academic Bibliography</div>
                      </button>

                      <button
                        type="button"
                        onClick={() => updateSetting("citationStyle", "standard")}
                        className={`p-2.5 rounded-lg border text-left transition-all ${
                          settings.citationStyle === "standard"
                            ? "border-primary bg-primary/5 font-semibold text-primary"
                            : "border-border/70 text-muted-foreground hover:bg-muted/40"
                        }`}
                      >
                        <div className="text-foreground font-medium">Standard Hanging</div>
                        <div className="text-[10px] text-muted-foreground">Traditional APA Indentation</div>
                      </button>
                    </div>
                  </div>

                  <div>
                    <span className="text-xs font-semibold text-foreground block mb-2">
                      Header Presentation
                    </span>
                    <div className="space-y-2 text-xs">
                      <label className="flex items-center justify-between p-2 rounded-lg border border-border/50 hover:bg-muted/30 cursor-pointer">
                        <span className="text-muted-foreground">Show "CURRICULUM VITAE" Top Banner</span>
                        <input
                          type="checkbox"
                          checked={settings.showCvHeaderBanner}
                          onChange={(e) => updateSetting("showCvHeaderBanner", e.target.checked)}
                          className="rounded border-border text-primary focus:ring-primary h-4 w-4"
                        />
                      </label>
                    </div>
                  </div>

                  <div>
                    <span className="text-xs font-semibold text-foreground block mb-2">
                      Header Contact Visibility
                    </span>
                    <div className="space-y-2 text-xs">
                      {[
                        { key: "showEmail", label: "Email Address" },
                        { key: "showPhone", label: "Phone Number" },
                        { key: "showLocation", label: "Office / Campus Address" },
                        { key: "showWebsite", label: "Personal Website Link" },
                        { key: "showScholar", label: "Google Scholar Profile" },
                        { key: "showOrcid", label: "ORCID ID Link" },
                        { key: "showLinkedin", label: "LinkedIn Profile" },
                        { key: "showGithub", label: "GitHub Profile" },
                      ].map((item) => (
                        <label
                          key={item.key}
                          className="flex items-center justify-between p-2 rounded-lg border border-border/50 hover:bg-muted/30 cursor-pointer"
                        >
                          <span className="text-muted-foreground">{item.label}</span>
                          <input
                            type="checkbox"
                            checked={Boolean(settings[item.key as keyof CVSettings])}
                            onChange={(e) =>
                              updateSetting(item.key as keyof CVSettings, e.target.checked as any)
                            }
                            className="rounded border-border text-primary focus:ring-primary h-4 w-4"
                          />
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <span className="text-xs font-semibold text-foreground block mb-2">
                      Section Content Options
                    </span>
                    <div className="space-y-2 text-xs">
                      {[
                        { key: "showPublicationBadges", label: "Show Publication Type Badges ([Journal], [Conference])" },
                        { key: "showAbstracts", label: "Include Publication Abstracts" },
                        { key: "showGrantsAmount", label: "Display Grant Award Monetary Amounts" },
                        { key: "showCourseCodes", label: "Display Course Codes in Teaching" },
                      ].map((item) => (
                        <label
                          key={item.key}
                          className="flex items-center justify-between p-2 rounded-lg border border-border/50 hover:bg-muted/30 cursor-pointer"
                        >
                          <span className="text-muted-foreground">{item.label}</span>
                          <input
                            type="checkbox"
                            checked={Boolean(settings[item.key as keyof CVSettings])}
                            onChange={(e) =>
                              updateSetting(item.key as keyof CVSettings, e.target.checked as any)
                            }
                            className="rounded border-border text-primary focus:ring-primary h-4 w-4"
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Reset to Defaults button */}
              <div className="pt-2 border-t border-border/50 flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResetSettings}
                  className="text-[11px] text-muted-foreground hover:text-foreground h-8 gap-1.5"
                >
                  <RotateCcw className="h-3 w-3" />
                  <span>Reset to Standard Academic Format</span>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right Live PDF Document Preview (7 Columns) ── */}
        <div className="lg:col-span-7">
          <div className="rounded-xl border border-border/80 bg-card shadow-xs overflow-hidden">
            {/* Document Preview Top Bar */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/60 bg-muted/20 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" />
                <span className="font-semibold text-foreground">Interactive Document Preview</span>
              </div>
              <div className="flex items-center gap-2 text-[11px]">
                <span className="px-2 py-0.5 rounded bg-muted text-muted-foreground font-mono">
                  {settings.pageSize}
                </span>
                <span>•</span>
                <span className="capitalize">{settings.template.replace("-", " ")}</span>
                <span>•</span>
                <span className="capitalize">{settings.density}</span>
              </div>
            </div>

            {/* Embedded Live PDFViewer with dynamic previewKey to trigger immediate re-compilation */}
            {hasPdfError ? (
              <div className="h-[780px] flex flex-col items-center justify-center text-center p-6 space-y-3 bg-muted/10">
                <AlertCircle className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm font-semibold text-foreground">Preview could not be displayed</p>
                <p className="text-xs text-muted-foreground max-w-sm">
                  Your browser may block embedded PDF rendering. You can still download the full PDF using the Download button above.
                </p>
                <Button variant="outline" size="sm" onClick={() => setHasPdfError(false)}>
                  Retry Preview
                </Button>
              </div>
            ) : (
              <div className="h-[780px] w-full bg-muted/20">
                <PDFViewer
                  key={previewKey}
                  style={{ width: "100%", height: "100%", border: "none" }}
                  showToolbar={true}
                >
                  <AcademicCV
                    profile={profile}
                    sections={sections}
                    settings={{ ...settings, includedSectionIds: effectiveIncludedIds }}
                  />
                </PDFViewer>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
